import type { CatalogImportResult } from '@wms/domain';
import { useState } from 'react';
import { api, apiBlob, errorMessage } from '../../api';
import { downloadBlob, fileToBase64 } from '../../files';
import { fmt } from '../../format';
import { useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { SellerSelect, useSellerPick } from './SellerPick';

const MAX_BYTES = 5 * 1024 * 1024;
const ACTION: Record<CatalogImportResult['preview'][number]['action'], { label: string; tone: string }> = {
  create: { label: 'Yeni', tone: 'ok' },
  update: { label: 'Güncellenecek', tone: 'info' },
  unchanged: { label: 'Değişmeyecek', tone: '' },
  error: { label: 'Hatalı', tone: 'bad' },
};

/**
 * Bulk catalog upload: download the Excel template, fill it in (or export CSV from another
 * system), check it — nothing is written while a row has a problem — then import.
 */
export function ImportProducts() {
  const toast = useToast();
  const pick = useSellerPick();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CatalogImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const template = async () => {
    try {
      downloadBlob(await apiBlob('/api/products/import-template'), 'urun-yukleme-sablonu.xlsx');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const run = async (dryRun: boolean, chosen = file) => {
    if (!chosen) return;
    if (chosen.size > MAX_BYTES) return setError('Dosya 5 MB üzerinde; daha az satırla deneyin');
    setBusy(true);
    setError(null);
    try {
      const body = { fileName: chosen.name, contentBase64: await fileToBase64(chosen), ...(pick.staff && pick.tenantId ? { tenantId: pick.tenantId } : {}) };
      const answer = await api<CatalogImportResult>('POST', `/api/products/import?dryRun=${dryRun}`, body);
      setResult(answer);
      if (!dryRun && !answer.dryRun) toast(`${answer.created} ürün eklendi, ${answer.updated} ürün güncellendi`, 'ok');
    } catch (e) {
      setError(errorMessage(e));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const imported = result && !result.dryRun;
  const canImport = result && result.dryRun && result.errors.length === 0 && result.created + result.updated > 0;

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/urunler">
          <Icon name="back" /> {pick.staff ? 'Ürünler' : 'Ürünler ve stok'}
        </a>
        <SellerSelect pick={pick} />
      </div>
      <section className="card">
        <div className="card-b stack">
          <div className="steps-list">
            <div>
              <b>1. Şablonu indirin.</b> Excel dosyasında her sütunun ne istediği ikinci sayfada yazar. Başka bir sistemden aldığınız CSV dosyasını da
              yükleyebilirsiniz; sütun başlıkları şablondakiyle aynı ya da benzer olmalı (örneğin "Stok kodu", "Ürün adı", "Barkod").
            </div>
            <div>
              <button className="btn" onClick={() => void template()}>
                <Icon name="download" /> Excel şablonunu indir
              </button>
            </div>
            <div>
              <b>2. Dosyayı seçin ve kontrol edin.</b> Aynı SKU'lu ürün varsa güncellenir; boş bıraktığınız hücreler mevcut bilgiyi değiştirmez. Hiçbir
              satırda sorun kalmayana kadar hiçbir şey kaydedilmez.
            </div>
            <div className="row">
              <input
                type="file"
                accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={!pick.ready}
                onChange={(e) => {
                  const chosen = e.target.files?.[0] ?? null;
                  setFile(chosen);
                  setResult(null);
                  if (chosen) void run(true, chosen);
                }}
              />
              {file && (
                <button className="btn sm" disabled={busy} onClick={() => void run(true)}>
                  <Icon name="refresh" size={16} /> Yeniden kontrol et
                </button>
              )}
              {!pick.ready && <span className="hint bad">Önce ürünlerin hangi satıcıya ait olduğunu seçin</span>}
            </div>
          </div>
          {error && <div className="banner bad">{error}</div>}
          {busy && <div className="muted">Dosya okunuyor…</div>}
        </div>
      </section>

      {result && (
        <section className="card">
          <div className="card-h">
            <h2>{imported ? 'İçe aktarıldı' : 'Kontrol sonucu'}</h2>
            <span className="sub">{file?.name}</span>
          </div>
          <div className="card-b stack">
            <div className="grid">
              <div className="kpi">
                <span className="n">{fmt.n(result.rows)}</span>
                <span className="l">Satır</span>
              </div>
              <div className="kpi">
                <span className="n">{fmt.n(result.created)}</span>
                <span className="l">{imported ? 'Eklendi' : 'Yeni ürün'}</span>
              </div>
              <div className="kpi">
                <span className="n">{fmt.n(result.updated)}</span>
                <span className="l">{imported ? 'Güncellendi' : 'Güncellenecek'}</span>
              </div>
              <div className="kpi">
                <span className="n">{fmt.n(result.unchanged)}</span>
                <span className="l">Değişmeyen</span>
              </div>
              <div className={`kpi${result.errors.length > 0 ? ' alert' : ''}`}>
                <span className="n">{fmt.n(new Set(result.errors.map((e) => e.row)).size)}</span>
                <span className="l">Sorunlu satır</span>
              </div>
            </div>
            {result.ignoredColumns.length > 0 && (
              <div className="banner warn">Tanınmayan sütunlar okunmadı: {result.ignoredColumns.join(', ')}</div>
            )}
            {imported && (
              <div className="banner ok">
                <Icon name="check" />
                <div>
                  Ürünler kataloğa kaydedildi.{' '}
                  <a href="#/urunler">Ürünler listesine dön</a>
                </div>
              </div>
            )}
            {canImport && (
              <div className="row">
                <button className="btn primary" disabled={busy} onClick={() => void run(false)}>
                  <Icon name="upload" /> {result.created} yeni, {result.updated} güncel ürünü kaydet
                </button>
              </div>
            )}
            {result.errors.length > 0 && (
              <>
                <h3>Düzeltilmesi gerekenler</h3>
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr>
                        <th className="num">Satır</th>
                        <th>Sütun</th>
                        <th>Sorun</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.slice(0, 300).map((e, i) => (
                        <tr key={i}>
                          <td className="num">{e.row}</td>
                          <td>{e.column ?? '—'}</td>
                          <td>{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <h3>Satırlar</h3>
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th className="num">Satır</th>
                    <th>SKU</th>
                    <th>Ürün adı</th>
                    <th>Barkod</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {result.preview.map((p) => (
                    <tr key={p.row}>
                      <td className="num">{p.row}</td>
                      <td className="mono">{p.sku}</td>
                      <td>{p.name}</td>
                      <td className="mono">{p.barcode ?? '—'}</td>
                      <td>
                        <span className={`chip ${ACTION[p.action].tone}`}>{ACTION[p.action].label}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
